import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import { Close, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { securityService } from '../../services/api';

// Audit trail of detected screen-capture attempts. iOS only: on Android
// FLAG_SECURE blocks capture outright, so there is nothing to report there.
const CaptureAttemptsDialog = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ attempts: [], total: 0 });
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchAttempts = useCallback(async () => {
    if (!open) return;
    try {
      setLoading(true);
      setError('');
      const params = { page: page + 1, limit: rowsPerPage };
      if (typeFilter !== 'all') params.type = typeFilter;
      const response = await securityService.getCaptureAttempts(params);
      setData({
        attempts: response.attempts || [],
        total: response.pagination?.total || 0
      });
    } catch (err) {
      console.error('Error fetching capture attempts:', err);
      setError(err.response?.data?.error || t('settings.captureAttemptsFetchError'));
    } finally {
      setLoading(false);
    }
  }, [open, typeFilter, page, rowsPerPage, t]);

  useEffect(() => {
    if (open) {
      setTypeFilter('all');
      setPage(0);
      setRowsPerPage(25);
    }
  }, [open]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  const formatDateTime = (date) => (date ? new Date(date).toLocaleString() : '-');

  const customerLabel = (customer) => {
    if (!customer) return t('settings.guestAttempt');
    const parts = [customer.firstName, customer.lastName].filter(Boolean);
    return parts.join(' ') || customer.email || '-';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t('settings.captureAttempts')}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('settings.captureAttemptsDesc')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>{t('settings.attemptType')}</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(0);
              }}
              label={t('settings.attemptType')}
            >
              <MenuItem value="all">{t('settings.allAttempts')}</MenuItem>
              <MenuItem value="screenshot">{t('settings.screenshot')}</MenuItem>
              <MenuItem value="screen_recording">{t('settings.screenRecording')}</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchAttempts} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.attemptDate')}</TableCell>
                <TableCell>{t('settings.attemptCustomer')}</TableCell>
                <TableCell>{t('settings.attemptType')}</TableCell>
                <TableCell>{t('settings.attemptPlatform')}</TableCell>
                <TableCell>{t('settings.attemptAppVersion')}</TableCell>
                <TableCell>{t('settings.attemptIp')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : data.attempts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {t('settings.noCaptureAttempts')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.attempts.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{customerLabel(row.customer)}</Typography>
                      {row.customer?.email && (
                        <Typography variant="caption" color="text.secondary">
                          {row.customer.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          row.type === 'screenshot'
                            ? t('settings.screenshot')
                            : t('settings.screenRecording')
                        }
                        color={row.type === 'screenshot' ? 'warning' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{row.platform || '-'}</TableCell>
                    <TableCell>{row.appVersion || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.ipAddress || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data.total}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('common.rowsPerPage')}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CaptureAttemptsDialog;
